import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { usePageActive } from '../lib/pageActive';

/**
 * 입력 폼용 팝업. 화면 가운데에 띄운다.
 *
 * 왜: 폼을 페이지 위쪽에 인라인으로 열면 **아래로 스크롤한 상태에서는 눌러도 아무 일도
 * 안 일어난 것처럼 보인다**(2026-07-31 사용자 신고 — "액션이 눈에 안 띄어 찾아야 한다").
 * 팝업은 지금 보고 있는 자리에서 뜬다.
 *
 * 🔴 **여백(백드롭) 클릭으로 닫지 않는다.** 이 모달은 **입력을 품는다** — 실수로 바깥을 누른 것을
 * "닫기 의도"로 읽으면 입력이 사라진다.
 *
 * **Esc 는 닫는다 — 단, 입력한 게 있으면 묻는다**(2026-09-29 게시판 #37 3번. 07-30 에 「답답해하면 dirty 체크로
 * 완화한다」고 남겨 둔 그 완화다). Esc 는 바깥 클릭과 달리 실수로 누르기 어렵고, 입력 소실은 확인창이 막는다.
 * - 닫는 길은 여전히 **폼 자신의 닫기 버튼**이다 — 모달은 닫는 함수를 모른다(닫기는 폼마다 상태를 치운다).
 *   그래서 Esc 는 그 버튼을 눌러 준다: `data-modal-close` → 글자가 정확히 「닫기」 → 「취소」 순. 못 찾으면 **아무것도 안 한다**.
 * - 「입력한 게 있다」 = 이 팝업 안에서 input 이벤트가 한 번이라도 났다(타이핑·선택·체크).
 * - 팝업 위에 팝업이 뜨면 맨 위 것만 닫는다. 한글 조합 중(isComposing)의 Esc 는 조합 취소라 건드리지 않는다.
 */
const stack: number[] = [];
let seq = 0;

export default function Modal({
  children,
  /** 좌우 최대 폭. 노선 표처럼 넓은 폼은 `max-w-5xl`. */
  width = 'max-w-3xl',
  draggable = false,
}: {
  children: ReactNode;
  width?: string;
  /** 위쪽 손잡이로 팝업을 끌어 옮긴다(#139 — 뒤 목록을 보며 입력). */
  draggable?: boolean;
}) {
  const [off, setOff] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);
  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    drag.current = { x: e.clientX, y: e.clientY, ox: off.x, oy: off.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (d) setOff({ x: d.ox + e.clientX - d.x, y: d.oy + e.clientY - d.y });
  };
  const onUp = () => { drag.current = null; };
  // 옮기기는 relative left/top 으로 — transform 을 쓰면 안쪽의 fixed 미리보기(서류 보기)가 이 상자 안에 갇힌다.
  // 뒤 페이지가 같이 스크롤되면 어느 쪽을 움직이는지 알 수 없다. 열려 있는 동안만 잠근다.
  // 화면 탭(#168): 숨은 화면의 팝업은 살려 두되(입력 유지) 안 보이게 — 스크롤 잠금·Esc 대상에서도 빠진다.
  const active = usePageActive();
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);

  const box = useRef<HTMLDivElement>(null);
  const dirty = useRef(false);
  useEffect(() => {
    if (!active) return;
    const id = ++seq;
    stack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.isComposing || e.defaultPrevented) return;
      if (stack[stack.length - 1] !== id) return;
      const el = box.current;
      if (!el) return;
      const buttons = [...el.querySelectorAll<HTMLButtonElement>('button:not([disabled])')];
      const close = el.querySelector<HTMLElement>('[data-modal-close]')
        ?? buttons.find((b) => b.textContent?.trim() === '닫기')
        ?? buttons.find((b) => b.textContent?.trim() === '취소');
      if (!close) return;
      e.preventDefault();
      if (dirty.current && !window.confirm('입력한 내용이 저장되지 않습니다. 닫을까요?')) return;
      close.click();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const at = stack.indexOf(id);
      if (at >= 0) stack.splice(at, 1);
    };
  }, [active]);

  // `document.body` 로 띄운다 — 조상에 `transform`/`filter` 가 있으면 `fixed` 가 그 안에
  // 갇혀 화면 가운데가 아니라 엉뚱한 자리에 뜬다.
  return createPortal(
    // app-modal = 창 안 표에도 화면 표와 같은 줄·올림 색(globals.css #445·#446).
    <div className="app-modal fixed inset-0 z-50 overflow-y-auto bg-black/40 p-2 sm:p-4" hidden={!active}>
      <div ref={box} className={`mx-auto my-2 w-full sm:my-6 ${width}`} onInput={() => { dirty.current = true; }}
           onChange={() => { dirty.current = true; }}
           style={draggable ? { position: 'relative', left: off.x, top: off.y } : undefined}>
        {draggable && (
          <div className="mb-1 flex cursor-move touch-none select-none justify-center rounded-t-lg bg-white/80 py-1 text-xs text-ink-faint"
               onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
               title="끌어서 옮기기">
            ⠿ 끌어서 옮기기
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
